import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Section,
  Typography,
  LinearProgress,
  Grid,
  Button,
  Icon,
  ButtonGroup
} from "~/components/UI";
import Table from "./Table";
import { useTranslation } from "react-i18next";
import { useQueryParams, StringParam } from "use-query-params";
import Filter from "./Filter";
import { getSortStr, deleteEmptyFields } from "~/utils/helpers";
import RowsPerPage from "~/components/RowsPerPage";
import api from "~/api";

/**
 * @memberof EventsLog
 * @component
 * @desc EventsLog Controller.
 */

const Events = () => {
  const [loading, setLoading] = useState(false);
  const [queryParams] = useQueryParams({
    search: StringParam
  });
  const [query, setQuery] = useState<EventsLogJournalApi.GetEventsLogsQuery>(queryParams as EventsLogJournalApi.GetEventsLogsQuery);
  const data = useRef<EventsLogJournalApi.EventDto[]>();
  const pagination = useRef<Api.PaginationSchema>();
  const { t } = useTranslation();
  const [limit, setLimit] = useState<number>(50);
  const [history, setHistory] = useState<any[]>([]);
  const [filterInitialValues] = useState(deleteEmptyFields(queryParams));

  const getEvents = useCallback(
    async (nextDatetime?: string) => {
      setLoading(true);
      try {
        const {
          data: { data: nextData, meta }
        } = await api.eventsLogJournal.getEvents(limit, {
          nextDatetime,
          ...{
            ...query,
            sort: query?.sort || "-timestamp"
          }
        });

        data.current = nextData;

        if (meta) {
          setHistory((history) => [...history, meta?.pagination?.nextDatetime]);
          pagination.current = meta.pagination;
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    },
    [query, data, limit]
  );

  const onSort = useCallback(
    (field: string, order: "desc" | "asc") => {
      data.current = [];
      setQuery({ ...query, sort: getSortStr(field, order) });
    },
    [query]
  );

  const onChangeFilters = useCallback((filters) => {
    pagination.current = undefined;
    data.current = [];
    setHistory([]);
    setQuery(filters);
  }, []);

  useEffect(() => {
    getEvents();
  }, [query, limit]);

  useEffect(() => {
    setHistory([]);
  }, [limit]);

  const onNext = () => {
    getEvents(history[history.length - 1]);
  };

  const onPrev = () => {
    getEvents(history[history.length - 3]);
    setHistory((history) => history.slice(0, -2));
  };

  return (
    <>
      <Box>
        <Section
          title={
            <Typography variant="h4">
              {t("title.eventsLog")}{" "}
              <Typography variant="h4" component="span" color="textSecondary">
                {pagination.current && pagination.current.totalCount}
              </Typography>
            </Typography>
          }
          extra={<Filter initialValues={filterInitialValues} onChangeFilter={onChangeFilters} />}
        />
        <LinearProgress hidden={!loading} />
      </Box>
      <Box flexGrow={1} overflow="auto">
        <Table dataSource={data.current} onSort={onSort} />
      </Box>
      <Box mt={2}>
        <Grid container justifyContent="space-between">
          <RowsPerPage initialValue={limit} onChange={setLimit} />
          <div>
            <ButtonGroup>
              <Button
                iconContainer={true}
                variant="contained"
                onClick={onPrev}
                disabled={history.length <= 1}
              >
                <Icon name="ArrowLeft" />
              </Button>
              <Button iconContainer={true} variant="contained" onClick={onNext}>
                <Icon name="ArrowRight" />
              </Button>
            </ButtonGroup>
          </div>
        </Grid>
      </Box>
    </>
  );
};

export default Events;
